/**
 * Main container component for the group events tab
 * Provides view toggle between calendar and table views
 */

import { useState, useMemo } from 'react'
import {
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
  Typography,
} from '@mui/material'
import {
  CalendarMonth as CalendarIcon,
  ViewList as ListIcon,
  Add as AddIcon,
} from '@mui/icons-material'
import { useGroupEvents } from '../../hooks/useGroupEvents'
import { EventsCalendarView } from './EventsCalendarView'
import { EventsTableView } from './EventsTableView'
import { CreateEventDialog } from './CreateEventDialog'
import { CalendarEventDto, CalendarEventSearchParams } from '@/api/types/calendarEvent'

type ViewMode = 'calendar' | 'table'
type SortableField = 'startDateTime' | 'title' | 'location' | 'attendees' | 'status'
type SortDirection = 'asc' | 'desc'

interface GroupEventsTabProps {
  groupId: string
  groupName?: string
  canViewEvents: boolean
  canCreateEvents: boolean
}

export function GroupEventsTab({
  groupId,
  groupName,
  canViewEvents,
  canCreateEvents,
}: GroupEventsTabProps) {
  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('calendar')
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date())
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  // Sorting and pagination state for table view
  const [sortBy, setSortBy] = useState<SortableField>('startDateTime')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)

  // Build search params based on current sort
  const searchParams: Partial<CalendarEventSearchParams> = useMemo(() => {
    const params: Partial<CalendarEventSearchParams> = {
      page: page + 1, // API uses 1-based pagination
      pageSize,
    }

    // Map our sort fields to API sort fields
    if (sortBy === 'startDateTime' || sortBy === 'title') {
      params.sortBy = sortBy === 'startDateTime' ? 'startDate' : sortBy
      params.sortDirection = sortDirection
    }

    return params
  }, [page, pageSize, sortBy, sortDirection])

  // Fetch events
  const { data, isLoading, error } = useGroupEvents(groupId, searchParams)

  // Extract events from the response
  const events: CalendarEventDto[] = data?.items || []
  const totalCount = data?.totalCount || 0

  const handleViewModeChange = (_: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
    if (newMode !== null) {
      setViewMode(newMode)
    }
  }

  const handleSortChange = (field: SortableField) => {
    if (sortBy === field) {
      // Toggle direction if same field
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      // New field, default to asc
      setSortBy(field)
      setSortDirection('asc')
    }
    setPage(0) // Reset to first page on sort change
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setPage(0) // Reset to first page
  }

  const handleCreateEvent = () => {
    setCreateDialogOpen(true)
  }

  // Sort events client-side for fields not supported by API
  const sortedEvents = useMemo(() => {
    if (sortBy === 'startDateTime' || sortBy === 'title') {
      // These are sorted by the API
      return events
    }

    // Client-side sorting for other fields
    return [...events].sort((a, b) => {
      let comparison = 0

      switch (sortBy) {
        case 'location':
          comparison = (a.location?.name || '').localeCompare(b.location?.name || '')
          break
        case 'attendees':
          comparison = (a.attendanceStats?.attendingCount || 0) - (b.attendanceStats?.attendingCount || 0)
          break
        case 'status':
          comparison = a.status - b.status
          break
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })
  }, [events, sortBy, sortDirection])

  if (!canViewEvents) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          You don't have permission to view events for this group.
        </Typography>
      </Box>
    )
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load events. Please try again later.
      </Alert>
    )
  }

  return (
    <Box>
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        {/* View Toggle */}
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          size="small"
        >
          <ToggleButton value="calendar">
            <CalendarIcon sx={{ mr: 0.5 }} />
            Calendar
          </ToggleButton>
          <ToggleButton value="table">
            <ListIcon sx={{ mr: 0.5 }} />
            List
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Create Event Button */}
        {canCreateEvents && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateEvent}
          >
            Create Event
          </Button>
        )}
      </Box>

      {/* View Content */}
      {viewMode === 'calendar' ? (
        <EventsCalendarView
          events={events}
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
          groupId={groupId}
        />
      ) : (
        <EventsTableView
          events={sortedEvents}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={handleSortChange}
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          groupId={groupId}
        />
      )}

      {/* Create Event Dialog */}
      <CreateEventDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        groupId={groupId}
        groupName={groupName}
      />
    </Box>
  )
}
